import { apiClient, getAuthenticatedAssetUrl } from "../../services/api";
import { resolveApiBase } from "../../utils/apiBase";

const IMAGE_MIME_PREFIX = "image/";
const VIDEO_MIME_PREFIX = "video/";
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "bmp", "svg"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "webm", "mov", "m4v", "ogg"]);

function normalizeMimeType(value) {
  return String(value || "").trim().toLowerCase();
}

const API_BASE = resolveApiBase();

function getFileExtension(value) {
  const normalized = String(value || "").split(/[?#]/)[0].trim().toLowerCase();
  const parts = normalized.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

function getBaseName(value) {
  const normalized = String(value || "")
    .trim()
    .replace(/\\/g, "/")
    .split(/[?#]/)[0];
  const parts = normalized.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

function parseContentDispositionFilename(headerValue) {
  const normalized = String(headerValue || "").trim();
  if (!normalized) return "";

  const utf8Match = normalized.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = normalized.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const unquotedMatch = normalized.match(/filename\s*=\s*([^;]+)/i);
  return unquotedMatch?.[1]?.trim() || "";
}

function buildAttachmentDownloadPath(path) {
  const normalized = String(path || "").trim();
  if (!normalized) return "";

  const [pathname, query = ""] = normalized.split("?");
  if (!/\/attachment$/i.test(pathname)) return normalized;

  return `${pathname}/download${query ? `?${query}` : ""}`;
}

function stripAssetTokenQuery(path) {
  const normalized = String(path || "").trim();
  if (!normalized) return "";

  try {
    const url = new URL(normalized, API_BASE || window.location.origin);
    url.searchParams.delete("token");

    if (/^https?:\/\//i.test(normalized)) {
      return url.toString();
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    const [pathname, query = ""] = normalized.split("?");
    if (!query) return pathname;

    const searchParams = new URLSearchParams(query);
    searchParams.delete("token");
    const nextQuery = searchParams.toString();
    return `${pathname}${nextQuery ? `?${nextQuery}` : ""}`;
  }
}

function inferAttachmentKind({ mimeType, attachmentUrl, attachmentName }) {
  if (mimeType.startsWith(IMAGE_MIME_PREFIX)) return "image";
  if (mimeType.startsWith(VIDEO_MIME_PREFIX)) return "video";

  const extension = getFileExtension(attachmentName) || getFileExtension(attachmentUrl);
  if (IMAGE_EXTENSIONS.has(extension)) return "image";
  if (VIDEO_EXTENSIONS.has(extension)) return "video";
  return "file";
}

export function formatAttachmentSize(bytes) {
  const parsed = Number(bytes);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";

  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(parsed) / Math.log(1024)), units.length - 1);
  const value = parsed / 1024 ** index;
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

function normalizeLabelPart(value) {
  if (value == null) return "";
  return String(value).trim();
}

function joinNameParts(...parts) {
  return parts
    .map(normalizeLabelPart)
    .filter(Boolean)
    .join(" ")
    .trim();
}

function firstNonEmptyLabel(...values) {
  return values.map(normalizeLabelPart).find(Boolean) || "";
}

export function resolveMediaUrl(path) {
  if (!path) return "";
  const normalizedPath = String(path).trim().replace(/\\/g, "/");
  if (!normalizedPath) return "";
  if (/^(javascript|data):/i.test(normalizedPath)) return "";

  const pathWithoutOrigin = normalizedPath.replace(/^https?:\/\/[^/]+/i, "");
  let trimmedPath = pathWithoutOrigin.replace(/^\.\//, "");

  if (/^https?:\/\//.test(normalizedPath)) return normalizedPath;

  // Support legacy API-prefixed asset paths.
  if (trimmedPath.startsWith("/api/uploads/") || trimmedPath.startsWith("/api/images/")) {
    trimmedPath = trimmedPath.replace(/^\/api/i, "");
  }

  if (trimmedPath.startsWith("/")) return `${API_BASE}${trimmedPath}`;

  const uploadsIndex = trimmedPath.indexOf("uploads/");
  if (uploadsIndex >= 0) {
    return `${API_BASE}/${trimmedPath.slice(uploadsIndex)}`;
  }

  const imagesIndex = trimmedPath.indexOf("images/");
  if (imagesIndex >= 0) {
    return `${API_BASE}/${trimmedPath.slice(imagesIndex)}`;
  }

  const messagingIndex = trimmedPath.indexOf("messaging/");
  if (messagingIndex >= 0) {
    return `${API_BASE}/uploads/${trimmedPath.slice(messagingIndex)}`;
  }

  const basename = getBaseName(trimmedPath);
  if (/^msg-\d+/i.test(basename)) {
    return `${API_BASE}/uploads/messaging/${basename}`;
  }

  return `${API_BASE}/images/${basename || trimmedPath}`;
}

export function getMessageAttachmentMeta(message, actorType = "SHOPPER") {
  const portal = actorType === "SELLER" ? "business" : "shop";
  const rawAttachmentUrl = firstNonEmptyLabel(
    message?.attachment_url,
    message?.attachmentUrl,
    message?.file_url,
    message?.fileUrl,
    message?.attachment?.url,
    message?.file?.url,
    message?.attachment_path,
    message?.attachmentPath,
  );
  const rawDownloadUrl = firstNonEmptyLabel(
    message?.attachment_download_url,
    message?.attachmentDownloadUrl,
    message?.file_download_url,
    message?.fileDownloadUrl,
    message?.attachment?.downloadUrl,
    message?.file?.downloadUrl,
    buildAttachmentDownloadPath(rawAttachmentUrl),
  );
  const attachmentUrl = getAuthenticatedAssetUrl(resolveMediaUrl(rawAttachmentUrl), {
    portal,
  });
  const downloadUrl = getAuthenticatedAssetUrl(resolveMediaUrl(rawDownloadUrl), {
    portal,
  });
  const attachmentName = firstNonEmptyLabel(
    message?.attachment_name,
    message?.attachmentName,
    message?.file_name,
    message?.fileName,
    message?.filename,
    message?.attachment?.name,
    message?.file?.name,
    getBaseName(attachmentUrl)
  ) || "Attachment";
  const mimeType = normalizeMimeType(
    message?.attachment_type ||
    message?.attachmentType ||
    message?.file_type ||
    message?.fileType ||
    message?.attachment?.type ||
    message?.file?.type
  );
  const sizeLabel = formatAttachmentSize(
    message?.attachment_size ||
    message?.attachmentSize ||
    message?.file_size ||
    message?.fileSize ||
    message?.attachment?.size ||
    message?.file?.size
  );

  if (!attachmentUrl && !downloadUrl) {
    return {
      hasAttachment: false,
      url: "",
      downloadUrl: "",
      requestUrl: "",
      portal,
      label: attachmentName,
      mimeType,
      kind: "file",
      sizeLabel,
    };
  }

  return {
    hasAttachment: true,
    url: attachmentUrl || downloadUrl,
    downloadUrl: downloadUrl || attachmentUrl,
    requestUrl: stripAssetTokenQuery(downloadUrl || attachmentUrl),
    portal,
    label: attachmentName,
    mimeType,
    kind: inferAttachmentKind({ mimeType, attachmentUrl: attachmentUrl || downloadUrl, attachmentName }),
    sizeLabel,
  };
}

export async function downloadMessageAttachment(attachment) {
  const targetUrl = normalizeLabelPart(
    attachment?.requestUrl ||
    attachment?.downloadUrl ||
    attachment?.url
  );
  if (!targetUrl) {
    throw new Error("This attachment is not available for download right now.");
  }

  try {
    const response = await apiClient.get(targetUrl, {
      responseType: "blob",
      headers: { "X-Portal": attachment?.portal === "business" ? "business" : "shop" },
    });
    const blob = response.data;
    const objectUrl = URL.createObjectURL(blob);
    const fileName =
      parseContentDispositionFilename(response.headers?.["content-disposition"]) ||
      normalizeLabelPart(attachment?.label) ||
      "attachment";

    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1000);
    return true;
  } catch (error) {
    throw error;
  }
}

function getParticipantName(conversation) {
  return firstNonEmptyLabel(
    conversation?.participant_name,
    conversation?.display_name,
    conversation?.business_name,
    conversation?.store_name,
    conversation?.seller_name,
    conversation?.shopper_name,
    conversation?.full_name,
    conversation?.username,
    conversation?.participant_slug,
    conversation?.business_slug,
    conversation?.slug
  );
}

function getParticipantAvatarPath(conversation) {
  return firstNonEmptyLabel(
    conversation?.participant_avatar_url,
    conversation?.avatar_url,
    conversation?.logo_url
  );
}

export function getConversationDisplayName(conversation, actorType = "SHOPPER") {
  if (actorType === "SELLER") {
    const fullName = joinNameParts(conversation?.first_name, conversation?.last_name);
    const shopperName = firstNonEmptyLabel(
      conversation?.shopper_name,
      conversation?.full_name,
      fullName,
      conversation?.participant_type === "SHOPPER" ? conversation?.participant_name : "",
      conversation?.display_name,
      conversation?.username,
      conversation?.shopper_email,
      conversation?.email
    );

    return shopperName || `Shopper ${conversation?.shopper_id || ""}`.trim();
  }

  const businessLabel = firstNonEmptyLabel(
    conversation?.business_name,
    conversation?.store_name,
    conversation?.seller_name,
    conversation?.owner_name,
    conversation?.participant_type === "BUSINESS" ? conversation?.participant_name : "",
    conversation?.display_name,
    getParticipantName(conversation),
    conversation?.business_slug,
    conversation?.slug
  );

  return businessLabel || `Shop ${conversation?.business_id || ""}`.trim();
}

export function getConversationSearchTokens(conversation, actorType = "SHOPPER") {
  const fullName = joinNameParts(conversation?.first_name, conversation?.last_name);

  if (actorType === "SELLER") {
    return [
      getConversationDisplayName(conversation, actorType),
      conversation?.participant_name,
      conversation?.display_name,
      conversation?.shopper_name,
      conversation?.full_name,
      fullName,
      conversation?.first_name,
      conversation?.last_name,
      conversation?.username,
      conversation?.shopper_email,
      conversation?.email,
      conversation?.last_message_text,
    ].map(normalizeLabelPart).filter(Boolean);
  }

  return [
    getConversationDisplayName(conversation, actorType),
    conversation?.participant_name,
    conversation?.display_name,
    conversation?.business_name,
    conversation?.store_name,
    conversation?.seller_name,
    conversation?.owner_name,
    fullName,
    conversation?.participant_slug,
    conversation?.business_slug,
    conversation?.slug,
    conversation?.last_message_text,
  ].map(normalizeLabelPart).filter(Boolean);
}

export function getConversationInitials(conversation, actorType = "SHOPPER") {
  const name = getConversationDisplayName(conversation, actorType);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

  return initials || (actorType === "SELLER" ? "S" : "B");
}

export function getConversationAvatarUrl(conversation, actorType = "SHOPPER") {
  const avatarPath = actorType === "SELLER"
    ? firstNonEmptyLabel(conversation?.participant_avatar_url, conversation?.avatar_url)
    : getParticipantAvatarPath(conversation);
  return resolveMediaUrl(avatarPath);
}

export function getConversationUnreadValue(conversation, actorType = "SHOPPER") {
  const field = actorType === "SELLER" ? "seller_unread_count" : "shopper_unread_count";
  return Number(conversation?.[field] || 0);
}

export function formatConversationPreview(conversation) {
  return conversation?.last_message_text || "No messages yet";
}

export function formatConversationTimestamp(dateValue) {
  if (!dateValue) return "";

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "";

  const now = new Date();
  if (parsed.toDateString() === now.toDateString()) {
    return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (parsed.toDateString() === yesterday.toDateString()) return "Yesterday";

  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatThreadTimestamp(dateValue) {
  if (!dateValue) return "";

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "";

  const now = new Date();
  const isToday = parsed.toDateString() === now.toDateString();

  if (isToday) {
    return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDayDivider(dateValue) {
  if (!dateValue) return "";

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "";

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (parsed.toDateString() === today.toDateString()) return "Today";
  if (parsed.toDateString() === yesterday.toDateString()) return "Yesterday";

  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
